import { WidgetContainer } from '../../../infrastructure/widgets/WidgetContainer';
import { WidgetConfig } from '../../../infrastructure/contracts/widget.contract';
import { ChartRenderer, ChartType, ChartDataSeries } from '../../../infrastructure/widgets/cards/ChartCard';
import { DeferredData } from '../../../infrastructure/adapters/DeferredDataAdapter';
import { WidgetEmpty } from '../../../infrastructure/widgets/WidgetStates';

export interface ChartWidgetData {
  categories: string[];
  series: ChartDataSeries[];
}

export interface SharedChartWidgetProps {
  config: WidgetConfig;
  dataQuery: {
    data?: DeferredData<ChartWidgetData>;
    isLoading: boolean;
    isError: boolean;
    error?: Error | null;
    refetch?: () => void;
  };
  chartType: ChartType;
  emptyMessage?: string;
}

export function SharedChartWidget({
  config,
  dataQuery,
  chartType,
  emptyMessage = 'No chart data available.',
}: SharedChartWidgetProps) {
  const { data: deferred, isLoading, isError, error, refetch } = dataQuery;

  const isWidgetLoading = isLoading || deferred?.loading;
  const widgetError = isError 
    ? error?.message 
    : deferred?.error;

  const chartData = deferred?.data;
  const hasData = chartData && chartData.series.length > 0 && chartData.categories.length > 0;

  return (
    <WidgetContainer
      config={config}
      isLoading={isWidgetLoading}
      isError={!!widgetError}
      errorMessage={widgetError}
      onRefresh={refetch}
    >
      {deferred?.unavailable ? (
        <div className="flex h-full items-center justify-center p-4">
          <p className="text-body-sm text-on-surface-variant italic text-center">
            Chart data unavailable.
          </p>
        </div>
      ) : !hasData ? (
        <WidgetEmpty message={emptyMessage} />
      ) : (
        <div className="flex-1 min-h-[200px] mt-2">
          <ChartRenderer 
            type={chartType} 
            categories={chartData.categories} 
            series={chartData.series} 
          />
        </div>
      )}
    </WidgetContainer>
  );
}
